/**
 * Login / SSO helper utilities.
 *
 * SSO pending state is stored in sessionStorage so it survives the IdP
 * redirect round-trip but does not leak across browser sessions.
 *
 * Authenticated principal state is cached in localStorage (with a TTL,
 * matching the upstream CloudIDP token lifetime of ~30min) so that
 * navigating between pages / opening new tabs / reloading the SPA
 * within that window does NOT re-trigger a full SSO round-trip — the
 * router guard can trust the cache and skip the /sso/v2/principal probe.
 * This matches the cfes-admin-frontend-vue3 behaviour.
 */

const SSO_PENDING_KEY = 'fod.admin.sso.pending'
// 5 minutes - max time to wait for IdP redirect back
const SSO_PENDING_TIMEOUT = 5 * 60 * 1000

// Persisted auth state cache. CloudIDP tokens are valid for 30min, so
// we cache the principal for 30min and refresh transparently on expiry.
export const AUTH_STATE_KEY = 'fod.admin.auth.state'
export const AUTH_STATE_TIMEOUT = 30 * 60 * 1000

export const markSSOPending = () => {
  try {
    sessionStorage.setItem(SSO_PENDING_KEY, Date.now().toString())
  } catch (_) { /* ignore */ }
}

export const isSSOPending = () => {
  try {
    const t = sessionStorage.getItem(SSO_PENDING_KEY)
    if (!t) return false
    return (Date.now() - parseInt(t, 10)) < SSO_PENDING_TIMEOUT
  } catch (_) {
    return false
  }
}

export const clearSSOPending = () => {
  try {
    sessionStorage.removeItem(SSO_PENDING_KEY)
  } catch (_) { /* ignore */ }
}

/**
 * Returns true if the cached auth-state object is still within its TTL.
 * Shape: { authenticated: true, timestamp: <ms>, data: <principal> }
 */
export const isAuthStateValid = (cachedAuthState, timeoutMs = AUTH_STATE_TIMEOUT) => {
  if (!cachedAuthState || !cachedAuthState.authenticated) return false
  if (!cachedAuthState.timestamp) return false
  return (Date.now() - cachedAuthState.timestamp) < timeoutMs
}

export const readAuthState = () => {
  try {
    const raw = localStorage.getItem(AUTH_STATE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (_) {
    return null
  }
}

export const writeAuthState = (principal) => {
  try {
    localStorage.setItem(AUTH_STATE_KEY, JSON.stringify({
      authenticated: true,
      timestamp: Date.now(),
      data: principal,
    }))
  } catch (_) { /* ignore */ }
}

export const clearAuthState = () => {
  try {
    localStorage.removeItem(AUTH_STATE_KEY)
  } catch (_) { /* ignore */ }
}
